import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage'
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth'

/**
 * Is a Capacitor plugin actually compiled into this native binary?
 *
 * Deliberately NOT `Capacitor.isPluginAvailable()`: that also returns true when a
 * plugin registers only a JS implementation, and several of ours register one for
 * native platforms too. Measured on an old iOS shell (1.4.4 build 83/84) it
 * answered true for SecureStorage and BiometricAuthNative while neither appeared
 * in PluginHeaders - so code trusting it still took the native path and hung the
 * app on the login screen with an infinite promise recursion inside the plugin.
 *
 * `PluginHeaders` is injected by the native bridge and lists exactly what the
 * binary carries, so it is the only honest answer. Anything unreadable returns
 * false - assume the plugin is absent and degrade to the web path, rather than
 * call into something that can never answer.
 *
 * Exported because shells released before a plugin existed still load the current
 * web bundle: `isNativePlatform()` is true there while the plugin is not, so every
 * caller of a native plugin needs this check, not just biometrics.
 */
export const hasNativePlugin = (name) => {
  try {
    const headers = Capacitor.PluginHeaders
    return Array.isArray(headers) && headers.some((plugin) => plugin?.name === name)
  } catch (e) {
    return false
  }
}

const VAULT_KEY = 'biometric_vault'
// v1 stored a refresh token. That could not survive an explicit logout, which
// deletes the token server-side, so v2 stores the sign-in credentials instead
// and replays a real login. Bumping the version makes stale v1 entries read as
// "not enrolled", so those users are simply offered enrollment again.
// v3 pairs the vault with an install marker (see below).
const VAULT_VERSION = 3

// An opaque, random id written both into the secure vault and into the app's
// native preferences (UserDefaults on iOS, SharedPreferences on Android), used
// only to detect a reinstall.
//
// This matters on iOS: Keychain items live outside the app container and
// therefore SURVIVE deleting the app, while preferences do not. So if the vault
// carries an id the app no longer knows, it was reinstalled (or its data
// cleared) and the leftover credentials must be discarded rather than used to
// sign in as whoever used the device before. On Android the Keystore-backed
// entry is already removed with the app, so the check is a harmless no-op there
// - kept uniform rather than branching per platform.
//
// Native preferences rather than localStorage because they are owned by the app
// instead of by the web origin: the WebView can evict site storage under
// pressure, which would silently un-enroll the user, and it would also be lost
// if the hosted origin ever changed.
//
// Deliberately holds no personal data - not the email and certainly not the
// password, both of which stay in the secure vault. A random id is useless to
// anyone who reads it, and it cannot be forged into a match without also
// reading the vault.
const INSTALL_ID_KEY = 'biometric_install_id'

// Guarded on the plugin, not just the platform: this module runs at import time,
// and on a shell without SecureStorage the call recurses through promise
// continuations instead of rejecting, so the .catch below would never fire.
if (Capacitor.isNativePlatform() && hasNativePlugin('SecureStorage')) {
  // iOS only - a documented no-op on Android, and the .catch keeps a rejection
  // harmless. Strongest accessibility this plugin offers: the item requires a
  // device passcode to exist, never leaves this device, is absent from backups
  // restored elsewhere, and is destroyed if the user removes their passcode.
  // Safe to require because biometry implies a passcode is set.
  // Android equivalent is covered by android:allowBackup="false" in the manifest.
  SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenPasscodeSetThisDeviceOnly)
    .catch(() => {})
}

function _mapBiometryType(biometryType) {
  if (biometryType === BiometryType.faceId || biometryType === BiometryType.faceAuthentication) {
    return 'face'
  }
  if (
    biometryType === BiometryType.touchId ||
    biometryType === BiometryType.fingerprintAuthentication
  ) {
    return 'fingerprint'
  }
  return null
}

async function _isAvailable() {
  if (!Capacitor.isNativePlatform() || !hasNativePlugin('BiometricAuthNative')) {
    return { available: false, type: null, notEnrolled: false }
  }

  const result = await BiometricAuth.checkBiometry()
  return {
    available: !!result.isAvailable,
    type: _mapBiometryType(result.biometryType),
    // The device has the sensor but the user has not registered a face or
    // finger with the OS yet. Settings tells them to do that rather than
    // hiding the option as if the hardware were missing.
    notEnrolled: result.code === 'biometryNotEnrolled',
    // Android refuses biometry app-wide for a cool-off period after repeated
    // failed attempts, and permanently until the device is unlocked with the
    // PIN. Both report as unavailable, so without the code the row would look
    // broken; with it, the row can say what to do and recover on its own.
    lockedOut: result.code === 'biometryLockout' || result.code === 'biometryLockoutPermanent',
  }
}

function _isUsableEnvelope(envelope) {
  return !!envelope &&
    envelope.version === VAULT_VERSION &&
    !!envelope.email &&
    !!envelope.password &&
    !!envelope.installId
}

// The install id is a convenience, not a security control: without Preferences it
// simply cannot be read or written, and the vault still works. Guarded so a shell
// carrying the biometric plugins but not Preferences degrades instead of calling
// into a plugin that is not there.
const _hasPreferences = () => hasNativePlugin('Preferences')

async function _readInstallId() {
  try {
    if (!_hasPreferences()) return null
    const { value } = await Preferences.get({ key: INSTALL_ID_KEY })
    return value
  } catch (e) {
    return null
  }
}

function _createInstallId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()

  // WKWebView before iOS 15.4 has no randomUUID, but getRandomValues is fine.
  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function _clear() {
  try {
    if (_hasPreferences()) await Preferences.remove({ key: INSTALL_ID_KEY })
  } catch (e) {
    // preferences unavailable; nothing to forget here
  }
  await SecureStorage.remove(VAULT_KEY)
}

/**
 * Reports whether a usable vault exists and which account it belongs to,
 * discarding anything inconsistent along the way.
 *
 * A Keychain entry whose install id is unknown to web storage means the app was
 * reinstalled (the Keychain outlives app deletion on iOS), so it is dropped
 * rather than trusted. The email is read back out of the Keychain, so no
 * personal data has to be kept outside it.
 */
async function _getVaultStatus() {
  const [installId, envelope] = await Promise.all([
    _readInstallId(),
    SecureStorage.get(VAULT_KEY),
  ])

  const consistent = _isUsableEnvelope(envelope) &&
    !!installId &&
    envelope.installId === installId

  if (!consistent) {
    if (envelope || installId) await _clear()
    return { enrolled: false, email: null }
  }

  return { enrolled: true, email: envelope.email }
}

async function _hasVault() {
  const { enrolled } = await _getVaultStatus()
  return enrolled
}

async function _enroll(credentials, reason) {
  const { email, password } = credentials || {}
  if (!email || !password) {
    throw new Error('Missing credentials for biometric enrollment')
  }

  await BiometricAuth.authenticate({ reason })

  const installId = _createInstallId()
  // Enrolling replaces whatever was stored before, so a device holds the saved
  // sign-in for one account at a time.
  await SecureStorage.set(VAULT_KEY, {
    version: VAULT_VERSION, email, password, installId,
  })

  try {
    if (_hasPreferences()) await Preferences.set({ key: INSTALL_ID_KEY, value: installId })
  } catch (e) {
    // Without the marker the vault reads as inconsistent and would be silently
    // discarded later, so fail loudly now instead of half-enrolling.
    await _clear()
    throw new Error('Could not save biometric login on this device')
  }
}

async function _authenticateAndRetrieve(reason) {
  const { enrolled } = await _getVaultStatus()
  if (!enrolled) {
    throw new Error('No biometric vault entry found')
  }

  await BiometricAuth.authenticate({ reason })

  const envelope = await SecureStorage.get(VAULT_KEY)
  if (!_isUsableEnvelope(envelope)) {
    throw new Error('No biometric vault entry found')
  }

  return { email: envelope.email, password: envelope.password }
}

const NativeBiometricAuthService = {
  isAvailable: _isAvailable,
  hasVault: _hasVault,
  getVaultStatus: _getVaultStatus,
  enroll: _enroll,
  authenticate: _authenticateAndRetrieve,
  clear: _clear,
}

export default NativeBiometricAuthService
